import { useQuery } from '@apollo/client'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import SectionTitle from '../../../components/SectionTitle'
import {
  PROMOTION_STATS_QUERY,
  PromotionStatsData
} from '../../../graphql/promotions'
import {
  formatCount,
  formatMinorWithCurrency
} from '../../../helpers/promotionAmount'
import { PERIOD_PRESETS, windowFor } from '../../../helpers/promotionPeriod'
import { getTranslate } from '../../../localization'
import useStyles from './styles/PromotionStatsPanel.styles'

// The local formatMinor is gone — it moved to helpers/promotionAmount when the Overview
// became a third consumer of the same Intl-based formatting. PromotionStats does not yet
// carry minorUnitDigits on the wire, so no server value is passed and the local table is
// used, which is what minorDigitsFor exists to announce when it goes stale.
const formatMinor = (minor: string, currency: string): string =>
  formatMinorWithCurrency(minor, currency)

// "142 of 500", or just "142" when uncapped. Zero means uncapped on the wire, and an
// operator reading "142 of 0" would think the campaign was misconfigured.
const ofCap = (used: number, cap: number): string =>
  cap > 0
    ? `${formatCount(used)} ${getTranslate('of')} ${formatCount(cap)}`
    : formatCount(used)

const Stat: React.FC<{ label: string; value: string; loading: boolean }> = ({
  label,
  value,
  loading
}) => {
  const classes = useStyles()
  return (
    <div className={classes.stat}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      {loading ? (
        <Skeleton animation="wave" width={90} />
      ) : (
        <Typography variant="h4">{value}</Typography>
      )}
    </div>
  )
}

/**
 * Campaign performance on the promotion record (AC-14, BL-146).
 *
 * Rendered only in edit mode — a promotion being created has no history, and a panel of
 * zeroes above an empty form reads as a broken query rather than as a new campaign.
 */
const PromotionStatsPanel: React.FC<{ promotionId: string }> = ({
  promotionId
}) => {
  const classes = useStyles()

  const [periodKey, setPeriodKey] = useState('all')

  // Recomputed only when the preset changes. Without the memo every render produces a new
  // `to` (it is Date.now), which changes the query variables and refetches on any parent
  // re-render — the form above this panel re-renders on every keystroke.
  const window = useMemo(
    () =>
      windowFor(PERIOD_PRESETS.find((p) => p.key === periodKey)?.days ?? null),
    [periodKey]
  )

  const { data, loading } = useQuery<PromotionStatsData>(
    PROMOTION_STATS_QUERY,
    {
      variables: { promotionId, from: window.from, to: window.to },
      fetchPolicy: 'network-only',
      // Conventions section 7: a failed query toasts. NOT zeroes — reporting a live
      // campaign as having zero uptake looks like a finding rather than a fault.
      onError: (err) => toast.error(err.message)
    }
  )

  const stats = data?.promotionStats

  // Keyed off the SERVER's echo, not off the local preset. They agree today, but the panel
  // is reporting what was actually applied — and if the two ever diverge, rendering the
  // local intent would hide it.
  const scoped = Boolean(stats?.periodFrom && stats?.periodTo)

  return (
    <Paper className={classes.paper}>
      <div className={classes.header}>
        <SectionTitle title={getTranslate('Campaign performance')} />
        <FormControl className={classes.period}>
          <InputLabel id="promotion-stats-period-label">
            {getTranslate('Period')}
          </InputLabel>
          <Select
            labelId="promotion-stats-period-label"
            id="promotion-stats-period"
            value={periodKey}
            onChange={(event) => setPeriodKey(event.target.value as string)}>
            {PERIOD_PRESETS.map((preset) => (
              <MenuItem key={preset.key} value={preset.key}>
                {getTranslate(preset.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className={classes.row}>
        {/*
          "of cap" is rendered ONLY for the lifetime view. The cap is a lifetime
          configuration and the count would be windowed, so "17 of 500" inside a 30-day
          window silently compares a month against the campaign's whole allowance — a
          number that reads as reassuring and is meaningless.
        */}
        <Stat
          loading={loading}
          label={getTranslate('Applied')}
          value={
            scoped
              ? formatCount(stats?.appliedCount ?? 0)
              : ofCap(stats?.appliedCount ?? 0, stats?.maxRedemptions ?? 0)
          }
        />
        <Stat
          loading={loading}
          label={getTranslate('Discount given')}
          value={formatMinor(
            stats?.discountMinor ?? '0',
            stats?.currency ?? ''
          )}
        />
        {/*
          In flight is shown SEPARATELY, never added to Applied. A reservation can still
          be released, so folding it in would overstate the campaign — and an operator
          reading a budget against its cap is making a commercial decision on it.
        */}
        {/*
          In flight is never windowed — it means right now, and a historical window has no
          in-flight state. The label says so when a window is active, because three numbers
          side by side otherwise imply they all cover the same span.
        */}
        <Stat
          loading={loading}
          label={
            scoped
              ? `${getTranslate('In flight')} (${getTranslate('now')})`
              : getTranslate('In flight')
          }
          value={`${formatCount(stats?.inFlightCount ?? 0)} (${formatMinor(
            stats?.inFlightDiscountMinor ?? '0',
            stats?.currency ?? ''
          )})`}
        />
      </div>
      {/* Lifetime configuration, same reason as the cap above. */}
      {!scoped && stats && stats.maxDiscountBudgetMinor !== '0' && (
        <Typography variant="caption" color="textSecondary">
          {getTranslate('Budget')}:{' '}
          {formatMinor(stats.maxDiscountBudgetMinor, stats.currency)}
        </Typography>
      )}
    </Paper>
  )
}

export default PromotionStatsPanel
